import { DataPoint } from "@glow/react/components/charts/SparkChart";
import { useZmClient } from "@components/hooks/useZmClient";
import { GlowGetApi, NftCollection, range, Solana } from "@glow/shared";
import { createUseAppContext } from "@utils/context";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import React, { createContext } from "react";
import { KeyedMutator } from "swr";

export enum NftCollectionTab {
  Nfts = "nfts",
  Sales = "sales",
}

const formatDataPoints = ({
  points: _points,
  valueType,
}: {
  points: GlowGetApi["/solana/nft/get-collection-historical-stats"]["response"]["points"];
  valueType: "average_price_sol" | "volume_sol";
}) => {
  const points = _points
    ?.filter((p) => {
      return (
        parseFloat(p.volume_sol) > 0 && parseFloat(p.average_price_sol) > 0
      );
    })
    .map((p) => {
      // reformat to match chart type
      return {
        timestamp: p.timestamp,
        value: parseFloat(p[valueType]),
      };
    });

  // We often don't have data in local dev so use dummy data
  if (
    process.env.NODE_ENV === "development" &&
    (!points || points.length === 0)
  ) {
    return range(30).map((idx) => ({
      timestamp: DateTime.now()
        .startOf("day")
        .minus({ days: 30 - idx })
        .toUTC()
        .toISO(),
      value: Math.random() * idx,
    }));
  }

  return points;
};

export const CollectionPageContext = createContext<{
  activeTab: NftCollectionTab;
  data: GlowGetApi["/solana/explore/get-nft-collection-info"]["response"];
  mutate: KeyedMutator<{
    collection: NftCollection.BasicInfo;
  }>;
  priceData?: GlowGetApi["/solana/nft/get-collection-historical-stats"]["response"];
  pricePoints?: DataPoint[];
  volumePoints?: DataPoint[];
  pricePointsHourly?: DataPoint[];
  volumePointsHourly?: DataPoint[];
  emptyCollectionFilters: boolean;
} | null>(null);

export const CollectionPageProvider = ({
  nftCollectionSymbol,
  initialData,
  children,
}: {
  nftCollectionSymbol: string;
  initialData: GlowGetApi["/solana/explore/get-nft-collection-info"]["response"];
  children: React.ReactNode;
}) => {
  const router = useRouter();

  const tabInUrl = (router.query.nftCollectionSymbol as string[])[1];
  const activeTab =
    tabInUrl === NftCollectionTab.Sales
      ? NftCollectionTab.Sales
      : NftCollectionTab.Nfts;

  const { data, mutate } = useZmClient({
    path: "/solana/explore/get-nft-collection-info",
    args: {
      magic_eden_symbol: nftCollectionSymbol,
      network: initialData.collection.network,
    },
    swrConfig: {
      fallbackData: initialData,
    },
  });

  const { data: priceData } = useZmClient({
    path: "/solana/nft/get-collection-historical-stats",
    args: {
      solana_nft_collection_api_id: data.collection.api_id,
    },
    pause: data.collection.network !== Solana.Network.Mainnet,
  });

  const pricePoints = formatDataPoints({
    points: priceData?.points ?? [],
    valueType: "average_price_sol",
  });
  const pricePointsHourly = formatDataPoints({
    points: priceData?.hourly_points ?? [],
    valueType: "average_price_sol",
  });

  const volumePoints = formatDataPoints({
    points: priceData?.points ?? [],
    valueType: "volume_sol",
  });
  const volumePointsHourly = formatDataPoints({
    points: priceData?.hourly_points ?? [],
    valueType: "volume_sol",
  });

  return (
    <CollectionPageContext.Provider
      value={{
        activeTab,
        data,
        mutate,
        priceData,
        pricePoints,
        volumePoints,
        pricePointsHourly,
        volumePointsHourly,
        emptyCollectionFilters: !(
          data.collection.traits_summary &&
          data.collection.traits_summary.length > 0
        ),
      }}
    >
      {children}
    </CollectionPageContext.Provider>
  );
};

export const useCollectionPageContext = createUseAppContext(
  CollectionPageContext,
);
